import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Header } from "@/components/layout/header";
import { Main } from "@/components/layout/main";
import { ProfileDropdown } from "@/components/profile-dropdown";
import { ThemeSwitch } from "@/components/theme-switch"; /*FIX AND ADD LATER*/
import { UploadCard } from "@/components/layout/uploadcard";
import { SchedulePostButton } from "@/components/schedule-post-button";
import { OnboardingModal } from "@/components/upload/onboarding-modal";
import { EditDraftModal } from "@/components/upload/edit-draft-modal";
import { useAuth } from "../auth/auth-context";
import { supabase } from "@/lib/supabaseClient";
import {
  fetchSupabaseDrafts,
  deleteSupabaseDraft,
} from "@/lib/supabaseDrafts";
import { resolveThumbnail } from "@/lib/resolveThumbnail";
import { useProducerProfile } from "@/lib/producer-profile";
import { toast } from "react-hot-toast";
import { useYouTubeDataCached } from "@/hooks/useYoutubeDataCached";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyStateGraphic from '@/assets/tEST_1-removebg-preview 2.svg';
import { HiPlusCircle } from "react-icons/hi";
import { ChannelOverview } from './channel-overview';
import { motion } from 'framer-motion';

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { displayName, loading: profileLoading } = useProducerProfile();
  
  const [uploads, setUploads] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<any>(null);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [manualRefreshCount, setManualRefreshCount] = useState(0);

 
  // Refs to prevent duplicate subscriptions and API calls
  const realtimeChannelRef = useRef<any>(null);
  const isRefreshingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Get video IDs for published uploads to fetch YouTube data
  const publishedVideoIds = uploads
    .filter(item => item.source === "upload" && item.youtube_video_id)
    .map(item => item.youtube_video_id);

  // FIXED: Only use requestPrioritySync for manual refreshes, remove auto-refresh
  const { 
    youtubeDataMap, 
    loading: youtubeDataLoading, 
    requestPrioritySync
  } = useYouTubeDataCached(publishedVideoIds);

  // Auth and routing check
  useEffect(() => {
    if (!authLoading && !user) {
      navigate({ to: "/sign-in" });
    }
  }, [user, authLoading, navigate]);

  // FIXED: Simplified realtime subscription - no auto-refreshes on updates
  useEffect(() => {
    if (!user?.id || realtimeChannelRef.current) return;

    console.log("Setting up realtime subscription for user:", user.id);

    const channelName = `uploads-${user.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'uploads',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Realtime upload change:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newUpload = payload.new as any;
            setUploads(prev => [{ ...newUpload, source: 'upload' }, ...prev]);
            toast.success(`Upload completed: ${newUpload.title || 'Untitled'}`);
            // REMOVED: Auto YouTube data refresh
          } 
          else if (payload.eventType === 'UPDATE') {
            const updatedUpload = payload.new as any;
            setUploads(prev => 
              prev.map(upload => 
                upload.youtube_video_id === updatedUpload.youtube_video_id 
                  ? { ...updatedUpload, source: 'upload' }
                  : upload
              )
            );
            // REMOVED: Toast notification for updates to reduce noise
          }
          else if (payload.eventType === 'DELETE') {
            const deletedUpload = payload.old as any;
            setUploads(prev => 
              prev.filter(upload => 
                upload.youtube_video_id !== deletedUpload.youtube_video_id
              )
            );
          }
        }
      )
      .subscribe((status) => {
        console.log(`Realtime subscription status: ${status}`);
        setRealtimeConnected(status === 'SUBSCRIBED');
      });

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        console.log('Cleaning up realtime subscription');
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [user?.id]);

 const refreshUploads = useCallback(async () => {
  if (!user?.id || isRefreshingRef.current) return;

  isRefreshingRef.current = true;
  console.log("Refreshing uploads for userId:", user.id);

  try {
    // Fetch drafts (unpublished)
    const drafts = (await fetchSupabaseDrafts(user.id)).filter(
      (d) => d.status !== "published" && !d.youtube_video_id
    );

    // Get user's active YouTube channel
    const { data: userToken } = await supabase
      .from('youtube_tokens')
      .select('channel_id, channel_name')
      .eq('user_id', user.id)
      .single();

    if (!userToken?.channel_id) {
      console.warn('No YouTube channel connected for user');
      // Only show drafts if no YouTube account connected
      setUploads(drafts.map((d) => ({ ...d, source: "draft" })));
      if (isInitialLoad) setIsInitialLoad(false);
      isRefreshingRef.current = false;
      return;
    }

    console.log(`Connected to YouTube channel: ${userToken.channel_name} (${userToken.channel_id})`);

    // Fetch uploads for this specific channel
    const { data: published, error } = await supabase
      .from("uploads")
      .select("*")
      .eq("user_id", user.id)
      .eq("youtube_channel_id", userToken.channel_id) // ← FILTER BY CHANNEL
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch uploads:", error);
      toast.error("Failed to load uploads");
      return;
    }

    console.log(`Fetched ${published?.length || 0} uploads for channel: ${userToken.channel_name}`);

    const combined = [
      ...drafts.map((d) => ({ ...d, source: "draft" })),
      ...(published || []).map((u) => ({ ...u, source: "upload" })),
    ];

    setUploads(combined);

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }

  } catch (error) {
    console.error("Error refreshing uploads:", error);
    toast.error("Failed to refresh uploads");
  } finally {
    isRefreshingRef.current = false;
  }
}, [user?.id, isInitialLoad]);

  // FIXED: Single initial load effect only
  useEffect(() => {
    if (user?.id && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      refreshUploads();
    }
  }, [user?.id, refreshUploads]);

  // Trigger onboarding once when profile loads
  useEffect(() => {
    if (
      !onboardingChecked &&
      !authLoading &&
      !profileLoading &&
      user &&
      (!displayName || displayName.trim() === "")
    ) {
      console.log("Display name missing - show onboarding");
      setShowOnboarding(true);
      setOnboardingChecked(true);
    } else if (user) {
      setOnboardingChecked(true);
    }
  }, [authLoading, profileLoading, user, displayName, onboardingChecked]);

  // FIXED: Only refresh uploads on success, no auto YouTube data refresh
  useEffect(() => {
    const handleRefresh = () => {
      console.log("Refresh triggered by success screen - uploads only");
      refreshUploads();
      // REMOVED: Auto YouTube data refresh
    };
    
    window.addEventListener("refresh-uploads", handleRefresh);
    return () => {
      window.removeEventListener("refresh-uploads", handleRefresh);
    };
  }, [refreshUploads]);

  // FIXED: Manual refresh function for user-initiated actions only
  const handleManualRefreshYouTubeData = useCallback(() => {
    if (publishedVideoIds.length === 0) {
      toast.info('No published videos to refresh');
      return;
    }
    
    console.log('User manually refreshing YouTube data for:', publishedVideoIds.length, 'videos');
    setManualRefreshCount(prev => prev + 1);
    requestPrioritySync(publishedVideoIds);
    toast.success('Requesting fresh YouTube data...');
  }, [publishedVideoIds, requestPrioritySync]);

  const handleDeleteDraft = async (id: string) => {
    if (!confirm("Are you sure you want to delete this draft?")) return;
    await deleteSupabaseDraft(id);
    refreshUploads();
  };

  const handleDeleteScheduled = async (videoId: string) => {
    if (!user) return;
    if (!confirm("Are you sure you want to delete this scheduled video? This will remove it from your dashboard and attempt to delete it from YouTube.")) return;

    setDeletingVideoId(videoId);

    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5002";
      const res = await fetch(
        `${backendUrl}/youtube-delete?userId=${user.id}&videoId=${videoId}`,
        { method: "DELETE" }
      );

      const result = await res.json();

      if (!res.ok) {
        if (result.code === 'QUOTA_EXCEEDED') {
          toast.error("YouTube API quota exceeded. Video removed from dashboard. You can delete it manually from YouTube Studio.", {
            duration: 8000
          });
          refreshUploads();
          return;
        }
        
        throw new Error(result.error || "Failed to delete video");
      }

      toast.success("Video deleted successfully.");
      refreshUploads();
      
    } catch (err: any) {
      console.error("Error deleting video:", err.message);
      toast.error(err.message || "Error deleting video.");
    } finally {
      setDeletingVideoId(null);
    }
  };

  const handleDeletePublished = async (videoId: string) => {
    if (!user) return;
    if (!confirm("Are you sure you want to delete this published video? This will permanently remove it from YouTube and cannot be undone.")) return;

    setDeletingVideoId(videoId);

    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5002";
      const res = await fetch(
        `${backendUrl}/youtube-delete?userId=${user.id}&videoId=${videoId}`,
        { method: "DELETE" }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to delete video");
      }

      toast.success("Video deleted successfully from YouTube and database.");
      refreshUploads();
      
    } catch (err: any) {
      console.error("Error deleting published video:", err.message);
      
      if (err.message.includes('YouTube authorization expired')) {
        toast.error("YouTube authorization expired. Please reconnect your account.");
      } else if (err.message.includes('permission')) {
        toast.error("You don't have permission to delete this video.");
      } else {
        toast.error(err.message || "Error deleting video.");
      }
    } finally {
      setDeletingVideoId(null);
    }
  };

  const getEffectiveStatus = (item: any): "draft" | "scheduled" | "published" => {
    if (item.source === "upload") {
      if (item.status === "scheduled" && item.scheduled_time) {
        const scheduledTime = new Date(item.scheduled_time);
        if (scheduledTime <= new Date()) return "published";
      }
      return item.status as "scheduled" | "published";
    }
    if (item.status === "scheduled" && item.scheduledAt) {
      const scheduledTime = new Date(item.scheduledAt);
      if (scheduledTime <= new Date()) return "published";
    }
    return item.status || "draft";
  };
 
  const filteredUploads = (
    statusFilter === "all"
      ? uploads
      : uploads.filter((d) => getEffectiveStatus(d) === statusFilter)
  )
    .sort((a, b) => {
      const dateA = new Date(a.created_at || a.savedAt).getTime();
      const dateB = new Date(b.created_at || b.savedAt).getTime();
      return dateB - dateA;
    });

  if (authLoading) return <div className="p-8">Checking authentication...</div>;

  return (
    <>
    
      <Header>
        <div className="ml-auto flex items-center space-x-4">
          <ProfileDropdown />
        </div>
      </Header>

      <div className='flex justify-between h-[calc(100vh-80px)] gap-2'>
        <Main className="flex-1 overflow-y-auto">
          <div className="flex flex-col rounded">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-medium">Recent Uploads</h1>
              <div className="flex gap-2">
                {process.env.NODE_ENV === 'development' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowOnboarding(true)}
                  >
                    Test Onboarding
                  </Button>
                )}
                <SchedulePostButton onAllowSchedule={() => window.dispatchEvent(new Event('open-upload-modal'))} />
              </div>
            </div>

            <Tabs
              defaultValue={statusFilter}
              onValueChange={(v) => setStatusFilter(v)}
              className="mb-8"
            >
              <TabsList className="bg-transparent p-1 gap-1">
                {["all", "draft", "scheduled", "published"].map((value) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className={`capitalize px-4 py-1 text-sm rounded  ${
                      statusFilter === value
                        ? "bg-white text-black font-medium"
                        : "text-white/60 hover:text-white font-medium"
                    }`}
                  >
                    {value === "all" ? "All Uploads" : value}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {filteredUploads.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 [@media(min-width:1300px)]:grid-cols-3 [@media(min-width:1600px)]:grid-cols-4 gap-x-4 gap-y-10">
                {filteredUploads.map((item, index) => {
                  const effectiveStatus = getEffectiveStatus(item);
                  const timestamp = 
                    effectiveStatus === "published"
                      ? `Published at ${new Date(
                          item.published_at || item.scheduled_time || item.created_at
                        ).toLocaleString("en-GB")}`
                      : effectiveStatus === "scheduled"
                      ? `Scheduled for ${new Date(item.scheduled_time).toLocaleString("en-GB")}`
                      : `Last edited ${new Date(
                          item.updated_at || item.savedAt
                        ).toLocaleString("en-GB")}`;

                  return (
                    <motion.div
                      key={item.id || item.youtube_video_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.3,
                        delay: index * 0.05,
                        ease: "easeOut"
                      }}
                    >
                      <UploadCard
                        title={item.title || "Untitled Draft"}
                        thumbnail={resolveThumbnail(item)}
                        timestamp={timestamp}
                        status={effectiveStatus}
                        youtube_video_id={item.youtube_video_id}
                        viewCount={item.cached_view_count}
                        likeCount={item.cached_like_count}
                        commentCount={item.cached_comment_count}
                        engagement={(() => {
                          const videoData = item.youtube_video_id ? youtubeDataMap[item.youtube_video_id] : undefined;
                          if (!videoData) return undefined;
                          
                          return {
                            views: videoData.viewCount || 0,
                            likes: videoData.likeCount || 0,
                            comments: videoData.commentCount || 0,
                            publishedAt: videoData.publishedAt || item.created_at,
                            dataSource: videoData.dataSource,
                            syncAge: videoData.syncAge
                          };
                        })()}
                        onRefreshStats={item.youtube_video_id ? () => {
                          console.log('Refreshing single video:', item.youtube_video_id);
                          requestPrioritySync([item.youtube_video_id]);
                        } : undefined}
                        isRefreshingStats={youtubeDataLoading}
                        onEdit={() => {
                          if (item.source === "draft") {
                            setSelectedDraft(item);
                            setIsEditModalOpen(true);
                          } else {
                            alert("Editing scheduled/published videos coming soon!");
                          }
                        }}
                        onDelete={() => {
                          const videoId = item.youtube_video_id;
                          
                          if (item.source === "draft") {
                            handleDeleteDraft(item.id);
                          } else if (effectiveStatus === "scheduled" && videoId) {
                            handleDeleteScheduled(videoId);
                          } else if (effectiveStatus === "published" && videoId) {
                            handleDeletePublished(videoId);
                          } else {
                            toast.error("Cannot delete this item.");
                          }
                        }}
                      />
                    </motion.div>
                  );
                })}
              </div>
            ) : isInitialLoad ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <img src={EmptyStateGraphic} alt="No uploads yet" className="w-40 h-40 mx-auto mb-8" />
                <h2 className="mb-4 text-2xl font-medium text-white">Looks like you don't have any beats scheduled just yet!</h2>
                <p className="mb-8 text-sm">Click the schedule button below to get started with uploading beats.</p>
                <div className="flex justify-center">
                  <SchedulePostButton onAllowSchedule={() => window.dispatchEvent(new Event('open-upload-modal'))} />
                </div>
              </div>
            )}
          </div>
        </Main>
        <ChannelOverview />
      </div>
      <OnboardingModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />
      <EditDraftModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        draftToEdit={selectedDraft}
        onDraftUpdated={refreshUploads}
      />
      
      {/* ✅ BackgroundUploader removed - now rendered globally by UploadQueueProvider */}
    </>
  );
}